import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { transcribeAudio } from "../../services/transcription";

function getSupportedMimeType() {
  if (typeof MediaRecorder === "undefined") return null;
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
  if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4";
  if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) return "audio/ogg;codecs=opus";
  return null;
}

const IDLE = "idle";
const RECORDING = "recording";
const TRANSCRIBING = "transcribing";
const MAX_RECORDING_SEC = 60;

function AudioRecordButton({ onTranscription, disabled = false }) {
  const { t } = useTranslation();
  const [state, setState] = useState(IDLE);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const stopRef = useRef(null);

  const supportedMimeType = getSupportedMimeType();

  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopTracks();
      clearTimer();
    };
  }, [stopTracks, clearTimer]);

  const stopAndTranscribe = useCallback(async () => {
    if (!mediaRecorderRef.current || state !== RECORDING) return;

    clearTimer();
    setElapsed(0);
    setState(TRANSCRIBING);

    const recorder = mediaRecorderRef.current;

    const blob = await new Promise((resolve) => {
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, {
          type: supportedMimeType,
        });
        chunksRef.current = [];
        resolve(audioBlob);
      };
      recorder.stop();
    });

    stopTracks();
    mediaRecorderRef.current = null;

    try {
      const result = await transcribeAudio(blob);
      if (result.text) {
        onTranscription(result.text);
      }
    } catch (error) {
      console.error("Transcription failed:", error);
    } finally {
      setState(IDLE);
    }
  }, [state, onTranscription, stopTracks, clearTimer, supportedMimeType]);

  // Keep stopRef in sync so the timer callback can call the latest version
  useEffect(() => {
    stopRef.current = stopAndTranscribe;
  }, [stopAndTranscribe]);

  const startRecording = useCallback(async () => {
    if (state !== IDLE || disabled) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream, {
        mimeType: supportedMimeType,
      });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start();
      setState(RECORDING);
      setElapsed(0);

      const startTime = Date.now();
      timerRef.current = setInterval(() => {
        const sec = Math.floor((Date.now() - startTime) / 1000);
        setElapsed(sec);
        if (sec >= MAX_RECORDING_SEC) {
          stopRef.current?.();
        }
      }, 1000);
    } catch (error) {
      console.error("Microphone access denied:", error);
      stopTracks();
    }
  }, [state, disabled, supportedMimeType, stopTracks]);

  const handleClick = useCallback(() => {
    if (state === IDLE) {
      startRecording();
    } else if (state === RECORDING) {
      stopAndTranscribe();
    }
  }, [state, startRecording, stopAndTranscribe]);

  const hasMediaDevices =
    typeof navigator !== "undefined" &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function";

  if (!supportedMimeType || !hasMediaDevices) return null;

  const isRecording = state === RECORDING;
  const isTranscribing = state === TRANSCRIBING;
  const remaining = MAX_RECORDING_SEC - elapsed;

  let title = "";
  if (isRecording) title = t("transcription.recording");
  else if (isTranscribing) title = t("transcription.transcribing");

  return (
    <button
      type="button"
      className={`btn ${isRecording ? "btn-danger" : "btn-default"}`}
      style={{
        minWidth: "44px",
        minHeight: "44px",
      }}
      title={title}
      disabled={disabled || isTranscribing}
      onClick={handleClick}
    >
      {isTranscribing ? (
        <i className="fa fa-spinner fa-spin fa-lg" />
      ) : isRecording ? (
        <>
          <i className="fa fa-stop fa-lg text-white" /> <span className="text-white">{remaining}s</span>
        </>
      ) : (
        <i className="fa fa-microphone fa-lg" />
      )}
    </button>
  );
}

AudioRecordButton.propTypes = {
  onTranscription: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default AudioRecordButton;
